// Curated palettes for tenant public sites — LIGHT premium designer studio look.
// Owners pick one; values become CSS variables on [data-tenant-theme].
//
// Token shape:
//   ink         — main text color (deep dark)
//   surface     — primary page background (cream/warm white)
//   surfaceAlt  — card / lifted surfaces (subtly tinted)
//   primary     — main accent (CTA, highlights)
//   primarySoft — tinted primary (glows, soft fills)
//   accent      — secondary accent (badges, pull-quotes)
//   border      — hairline border color (rgba)
//   muted       — secondary text color
//   heroBlobA/B — atmospheric blob colors (alpha for glow)

export const PALETTES = [
    {
        id: 'forest',
        name: 'Forest',
        nameBn: 'ফরেস্ট',
        description: 'Soft ivory paper · deep forest green',
        ink: '#0c1a12',
        surface: '#f8f6f0',
        surfaceAlt: '#ffffff',
        primary: '#0E5C2E',
        primarySoft: 'rgba(14, 92, 46, 0.10)',
        accent: '#C2A36B',
        border: 'rgba(12, 26, 18, 0.10)',
        muted: '#5b6a60',
        heroBlobA: 'rgba(14, 92, 46, 0.16)',
        heroBlobB: 'rgba(194, 163, 107, 0.18)'
    },
    {
        id: 'midnight_porcelain',
        name: 'Midnight Porcelain',
        nameBn: 'মিডনাইট',
        description: 'Bone white · graphite ink · cobalt accent',
        ink: '#0b1220',
        surface: '#f6f6f4',
        surfaceAlt: '#ffffff',
        primary: '#13244A',
        primarySoft: 'rgba(19, 36, 74, 0.09)',
        accent: '#C9A24A',
        border: 'rgba(11, 18, 32, 0.10)',
        muted: '#4f5566',
        heroBlobA: 'rgba(19, 36, 74, 0.14)',
        heroBlobB: 'rgba(201, 162, 74, 0.18)'
    },
    {
        id: 'crimson_atelier',
        name: 'Crimson Atelier',
        nameBn: 'ক্রিমসন',
        description: 'Warm cream · oxblood · brushed brass',
        ink: '#1a0d0d',
        surface: '#faf4ee',
        surfaceAlt: '#ffffff',
        primary: '#8B1A1A',
        primarySoft: 'rgba(139, 26, 26, 0.10)',
        accent: '#B98C46',
        border: 'rgba(26, 13, 13, 0.10)',
        muted: '#6a5a52',
        heroBlobA: 'rgba(139, 26, 26, 0.14)',
        heroBlobB: 'rgba(185, 140, 70, 0.18)'
    },
    {
        id: 'graphite_brass',
        name: 'Graphite & Brass',
        nameBn: 'গ্রাফাইট',
        description: 'Cool concrete · charcoal · warm brass',
        ink: '#111315',
        surface: '#f3f3ef',
        surfaceAlt: '#ffffff',
        primary: '#202428',
        primarySoft: 'rgba(32, 36, 40, 0.08)',
        accent: '#C58A3A',
        border: 'rgba(17, 19, 21, 0.10)',
        muted: '#52595f',
        heroBlobA: 'rgba(32, 36, 40, 0.10)',
        heroBlobB: 'rgba(197, 138, 58, 0.18)'
    },
    {
        id: 'indigo_studio',
        name: 'Indigo Studio',
        nameBn: 'ইন্ডিগো',
        description: 'Soft mist · deep indigo · warm sand',
        ink: '#0e1430',
        surface: '#f5f6fb',
        surfaceAlt: '#ffffff',
        primary: '#1F2A75',
        primarySoft: 'rgba(31, 42, 117, 0.10)',
        accent: '#D6B889',
        border: 'rgba(14, 20, 48, 0.10)',
        muted: '#525c80',
        heroBlobA: 'rgba(31, 42, 117, 0.13)',
        heroBlobB: 'rgba(214, 184, 137, 0.18)'
    },
    {
        id: 'sand_terracotta',
        name: 'Sand & Terracotta',
        nameBn: 'টেরাকোটা',
        description: 'Warm sand · terracotta · ink',
        ink: '#1c130d',
        surface: '#f8f1e8',
        surfaceAlt: '#fffbf5',
        primary: '#B14A26',
        primarySoft: 'rgba(177, 74, 38, 0.10)',
        accent: '#7C5A36',
        border: 'rgba(28, 19, 13, 0.10)',
        muted: '#6c5b4d',
        heroBlobA: 'rgba(177, 74, 38, 0.14)',
        heroBlobB: 'rgba(124, 90, 54, 0.18)'
    },
    {
        id: 'pure_mono',
        name: 'Pure Mono',
        nameBn: 'মোনো',
        description: 'Pure white · black · pick an accent',
        ink: '#0a0a0a',
        surface: '#ffffff',
        surfaceAlt: '#fafafa',
        primary: '#0a0a0a',
        primarySoft: 'rgba(10, 10, 10, 0.06)',
        accent: '#0a0a0a',
        border: 'rgba(10, 10, 10, 0.10)',
        muted: '#5e5e5e',
        heroBlobA: 'rgba(10, 10, 10, 0.04)',
        heroBlobB: 'rgba(10, 10, 10, 0.02)',
        accentSwatches: [
            { id: 'forest', label: 'Forest', value: '#0E5C2E' },
            { id: 'cobalt', label: 'Cobalt', value: '#1E40AF' },
            { id: 'ember', label: 'Ember', value: '#EA580C' },
            { id: 'rose', label: 'Rose', value: '#E11D48' },
            { id: 'plum', label: 'Plum', value: '#6D28D9' },
            { id: 'teal', label: 'Teal', value: '#0F766E' }
        ]
    }
];

export const DEFAULT_PALETTE_ID = 'forest';

export function getPalette(id) {
    return PALETTES.find(p => p.id === id) || PALETTES[0];
}

export function paletteToCssVars(palette, accentSwatchId) {
    let primary = palette.primary;
    let accent = palette.accent;
    let primarySoft = palette.primarySoft;
    let blobA = palette.heroBlobA;
    if (palette.id === 'pure_mono' && accentSwatchId) {
        const sw = palette.accentSwatches?.find(s => s.id === accentSwatchId);
        if (sw) {
            primary = sw.value;
            accent = sw.value;
            primarySoft = hexToRgba(sw.value, 0.10);
            blobA = hexToRgba(sw.value, 0.14);
        }
    }
    return {
        '--tenant-ink': palette.ink,
        '--tenant-surface': palette.surface,
        '--tenant-surface-alt': palette.surfaceAlt,
        '--tenant-primary': primary,
        '--tenant-primary-soft': primarySoft,
        '--tenant-accent': accent,
        '--tenant-border': palette.border,
        '--tenant-muted': palette.muted,
        '--tenant-blob-a': blobA,
        '--tenant-blob-b': palette.heroBlobB
    };
}

function hexToRgba(hex, alpha) {
    const m = hex.replace('#', '');
    const v = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
    const n = parseInt(v, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
